/**
 * @file StatusChip - Unified, config-driven status badge for the entire app.
 *
 * Renders a color-coded MUI Chip for any entity's status value. All status-to-
 * visual mappings live in the STATUS_CONFIGS lookup table, so adding a new status
 * or entity type requires zero component changes -- just extend the config.
 *
 * Supports five entity types (absence, user, expense, todo, delivery) with dual-
 * language keys (German DB values like "pendent" alongside English aliases like
 * "pending") so both legacy and newer code paths resolve correctly.
 *
 * @example
 *   <StatusChip status="pendent" type="absence" />
 *   <StatusChip status="approved" type="expense" showIcon={false} />
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Chip, useTheme } from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Close as RejectIcon,
  HourglassEmpty as PendingIcon,
  Done as DoneIcon,
  Warning as OverdueIcon,
  Schedule as OpenIcon,
} from '@mui/icons-material';

/**
 * Status configuration for different entity types.
 * Each type has status values mapped to MUI Chip colors, German labels, and optional icons.
 *
 * Structure: { [entityType]: { [statusKey]: { color, label, icon? } } }
 *
 * NOTE: Some types list both German and English keys (e.g. expense has "pendent"
 * and "pending") because Firestore documents may store either variant depending
 * on when they were created. Both must resolve to the same visual output.
 */
const STATUS_CONFIGS = {
  absence: {
    pendent: { color: 'warning', label: 'Ausstehend' },
    akzeptiert: { color: 'success', label: 'Genehmigt' },
    abgelehnt: { color: 'error', label: 'Abgelehnt' },
  },
  user: {
    online: { color: 'success', label: 'Online' },
    offline: { color: 'error', label: 'Offline' },
    pause: { color: 'warning', label: 'Pause' },
  },
  expense: {
    // German status values
    pendent: { color: 'warning', label: 'Ausstehend' },
    akzeptiert: { color: 'success', label: 'Genehmigt', icon: ApproveIcon },
    abgelehnt: { color: 'error', label: 'Abgelehnt', icon: RejectIcon },
    // English status values (used in Spesen.js)
    pending: { color: 'warning', label: 'Ausstehend' },
    approved: { color: 'success', label: 'Genehmigt', icon: ApproveIcon },
    rejected: { color: 'error', label: 'Abgelehnt', icon: RejectIcon },
  },
  todo: {
    // Status values used in Todos.js
    done: { color: 'success', label: 'Erledigt', icon: DoneIcon },
    overdue: { color: 'error', label: 'Überfällig', icon: OverdueIcon },
    open: { color: 'info', label: 'Offen', icon: OpenIcon },
    // Default/fallback for any other status
    active: { color: 'info', label: 'Offen', icon: OpenIcon },
    completed: { color: 'success', label: 'Erledigt', icon: DoneIcon },
  },
  delivery: {
    pending: { color: 'warning', label: 'Ausstehend' },
    delivered: { color: 'success', label: 'Geliefert' },
    cancelled: { color: 'error', label: 'Storniert' },
    // German variations
    offen: { color: 'warning', label: 'Offen' },
    abgeschlossen: { color: 'success', label: 'Abgeschlossen' },
  },
};

/**
 * Produces soft/pastel color styles for status chips by layering a semi-transparent
 * white gradient over the theme's light palette color. This avoids the high-contrast
 * look of MUI's default Chip `color` prop and keeps the UI scannable in dense tables.
 *
 * @param {'warning'|'success'|'error'|'info'} color - Semantic color key from STATUS_CONFIGS
 * @param {object} theme - MUI theme (from useTheme)
 * @returns {object} sx-compatible style object with bgcolor, color, and backgroundImage
 */
const getMildColorStyles = (color, theme) => {
  const colorMap = {
    warning: {
      bgcolor: theme.palette.warning.light,
      color: theme.palette.warning.dark,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
    },
    success: {
      bgcolor: theme.palette.success.light,
      color: theme.palette.success.dark,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
    },
    error: {
      bgcolor: theme.palette.error.light,
      color: theme.palette.error.dark,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
    },
    info: {
      bgcolor: theme.palette.info.light,
      color: theme.palette.info.dark,
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
    },
  };
  return colorMap[color] || {};
};

/**
 * StatusChip - Unified status display chip for various entity types
 *
 * @param {string} status - The status value (e.g., 'pendent', 'online')
 * @param {string} type - Entity type ('absence' | 'user' | 'expense' | 'todo' | 'delivery')
 * @param {string} size - Chip size ('small' | 'medium')
 * @param {boolean} showIcon - Whether to show icon if available (default: true)
 * @param {object} sx - Additional MUI sx styles
 */
function StatusChip({ status, type, size = 'small', showIcon = true, sx }) {
  const theme = useTheme();

  // Get configuration for the type
  const typeConfig = STATUS_CONFIGS[type];

  // Graceful degradation: unknown type or status renders an unstyled Chip
  // with the raw status string so the UI never breaks on unexpected data.
  if (!typeConfig) {
    return <Chip size={size} label={status} sx={sx} />;
  }

  const statusConfig = typeConfig[status];

  if (!statusConfig) {
    return <Chip size={size} label={status} sx={sx} />;
  }

  // Get the icon component if available and showIcon is true
  const IconComponent = showIcon && statusConfig.icon;

  // Get mild color styles
  const mildStyles = getMildColorStyles(statusConfig.color, theme);

  return (
    <Chip
      size={size}
      label={statusConfig.label}
      icon={IconComponent ? <IconComponent /> : undefined}
      sx={{
        ...mildStyles,
        '& .MuiChip-icon': {
          color: mildStyles.color,
        },
        ...sx,
      }}
    />
  );
}

StatusChip.propTypes = {
  /** The status value */
  status: PropTypes.string.isRequired,
  /** Entity type for status configuration */
  type: PropTypes.oneOf(['absence', 'user', 'expense', 'todo', 'delivery']).isRequired,
  /** Chip size */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Whether to show icon if available */
  showIcon: PropTypes.bool,
  /** Additional MUI sx styles */
  sx: PropTypes.object,
};

StatusChip.defaultProps = {
  size: 'small',
  showIcon: true,
  sx: undefined,
};

// Export STATUS_CONFIGS for external use if needed
export { STATUS_CONFIGS };

export default StatusChip;
